/**
 * "A new version is ready."
 *
 * `registerType: 'prompt'` in vite.config.ts only tells the plugin not to activate
 * a new service worker by itself. The prompt it is named after is UI the
 * application has to render — and nothing ever did, so there was no prompt and no
 * way to take the update.
 *
 * That was worse than `autoUpdate`, not merely incomplete. A new worker installed,
 * went to `waiting`, and browsers only promote a waiting worker once every window
 * of the app has closed. On an installed iOS PWA that means swiping it out of the
 * app switcher, which nobody does, so a phone could sit on a fortnight-old build
 * indefinitely with nothing on screen to say so. Deploys looked like no-ops.
 *
 * Still `prompt` and not `autoUpdate`, for the reason vite.config.ts gives:
 * swapping the bundle under someone mid-edit on an envelope loses their
 * keystrokes. Asking was always right. This asks.
 */

import { useRegisterSW } from 'virtual:pwa-register/react'
import { Portal } from '../lib/Portal.tsx'

/**
 * How often to look for a new deploy while the app stays open.
 *
 * A browser checks for a new worker on navigation, and an installed PWA can go days
 * without one — which would make the prompt depend on the very reload it exists to
 * ask for. Hourly is far more often than this gets deployed and costs one
 * conditional request.
 */
const CHECK_INTERVAL_MS = 60 * 60 * 1000

export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return
      setInterval(() => void registration.update(), CHECK_INTERVAL_MS)
    },
  })

  if (!needRefresh) return null

  return (
    // Through Portal, like UndoToast and for the same reason: `<main>` carries the
    // `.kete-enter` transform, which makes it a containing block for `position:
    // fixed` and would centre this in a register several screens tall.
    <Portal>
      <div
        // Polite: a new build is worth knowing about, not worth interrupting for.
        role="status"
        aria-live="polite"
        /*
         * Centred with `inset-x-4` + `mx-auto w-max`, not `left-1/2` and a
         * translate, for two independent reasons — the first version had both
         * faults at once.
         *
         * `left: 50%` with no `right` leaves a shrink-to-fit box only half the
         * viewport wide, so "A new version is ready." wrapped onto four lines on a
         * phone. And `.kete-pop[data-visible="true"]` sets `transform: none`, which
         * silently discards `-translate-x-1/2`, so it was not centred either.
         *
         * Giving it both edges and letting the margins do the centring is immune to
         * both: the box may use the full width less its gutters, and no animation
         * resetting `transform` can move it.
         *
         * Bottom clears the mobile tab bar — 56px plus its own safe-area padding.
         */
        className="kete-pop fixed inset-x-4 bottom-[calc(76px+env(safe-area-inset-bottom))] z-[80] mx-auto flex w-max max-w-full items-center gap-4 rounded-full border border-line bg-ink px-5 py-3 shadow-[var(--shadow-pop)] lg:bottom-6"
        data-visible="true"
      >
        <span className="text-[13px] text-paper">A new version is ready.</span>
        <button
          type="button"
          // `true` reloads once the new worker has taken over. Without it the page
          // keeps running the old bundle until the next navigation, which is the
          // confusion this component exists to end.
          onClick={() => void updateServiceWorker(true)}
          className="rounded-full bg-paper/15 px-3 py-1 text-[12px] text-paper transition-colors hover:bg-paper/25"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={() => setNeedRefresh(false)}
          aria-label="Not now"
          className="-mr-1 px-1 text-[13px] text-paper/50 transition-colors hover:text-paper"
        >
          ×
        </button>
      </div>
    </Portal>
  )
}
